import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  ScanBarcode,
  Plus,
  Calendar,
  Package,
  CheckCircle2,
  Trash2,
  ChevronLeft,
  Edit3,
  X,
  Camera,
  CameraOff,
  Loader2,
  ClipboardList,
  Hash,
  Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safe fetch helper - uses pre-cloned response to avoid "body stream already read"
async function safeFetch(url, options) {
  const res = await fetch(url, options);
  // Use the pre-cloned response (created before monitoring scripts consume the body)
  const safe = res._safeClone || res;
  try {
    const data = await safe.json();
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: res.ok, status: res.status, data: { detail: "Erro de conexão" } };
  }
}

// ==================== BARCODE SCANNER COMPONENT ====================
function BarcodeScanner({ onScan, active }) {
  const scannerRef = useRef(null);
  const lastScanRef = useRef(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!active) {
      // Stop scanner
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const startCamera = async () => {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");

        if (cancelled) return;

        const scanner = new Html5Qrcode("barcode-reader", {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.CODE_128,
          ],
          verbose: false,
        });

        scannerRef.current = scanner;

        // Use facingMode constraint to auto-select rear camera
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 100 },
            aspectRatio: 1.5,
            disableFlip: false,
          },
          (decodedText) => {
            const now = Date.now();
            if (now - lastScanRef.current > 2500) {
              lastScanRef.current = now;
              onScan(decodedText);
            }
          },
          () => {} // ignore scan errors (no barcode in frame)
        );

        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Erro ao acessar câmera");
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [active, onScan]);

  if (!active) return null;

  return (
    <div className="w-full">
      <div
        id="barcode-reader"
        className="w-full rounded overflow-hidden bg-black"
        style={{ minHeight: 250 }}
      />
      {error && (
        <p className="text-xs text-destructive mt-2 text-center">{error}</p>
      )}
      <p className="text-xs text-muted-foreground text-center mt-1">
        Aponte a câmera para o código de barras
      </p>
    </div>
  );
}

// ==================== EXPIRY CAMERA CAPTURE ====================
function ExpiryCameraCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      } catch (err) {
        if (!cancelled) setError("Erro ao acessar câmera: " + err.message);
      }
    };
    startCamera();
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const capturePhoto = () => {
    if (!videoRef.current) return;
    setCapturing(true);
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    // Stop camera
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    onCapture(dataUrl);
  };

  const handleClose = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="expiry-camera-modal">
      <div className="flex items-center justify-between p-3">
        <p className="text-white text-sm font-medium">Fotografe a data de validade</p>
        <button onClick={handleClose} className="p-1.5 text-white/80 hover:text-white" data-testid="close-expiry-camera">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        {error ? (
          <p className="text-white/60 text-sm px-4 text-center">{error}</p>
        ) : (
          <video ref={videoRef} className="w-full h-full object-cover" playsInline autoPlay muted />
        )}
      </div>
      {/* Capture guide overlay */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="border-2 border-white/50 rounded-lg" style={{ width: "70%", height: 80 }}>
          <p className="text-white/50 text-xs text-center mt-1">Alinhe a validade aqui</p>
        </div>
      </div>
      <div className="p-4 flex justify-center">
        <button
          onClick={capturePhoto}
          disabled={capturing || !!error}
          className="w-16 h-16 rounded-full bg-white flex items-center justify-center disabled:opacity-50"
          data-testid="capture-expiry-btn"
        >
          {capturing ? <Loader2 className="w-6 h-6 animate-spin text-primary" /> : <Camera className="w-7 h-7 text-primary" />}
        </button>
      </div>
    </div>
  );
}

// ==================== LOT ENTRY FORM ====================
function LotEntryForm({ ean, productName, found, onSubmit, onCancel }) {
  const [entries, setEntries] = useState([{ id: 1, expiryDate: "", capturedImage: null, imagePath: null }]);
  const [submitting, setSubmitting] = useState(false);
  const [showExpiryCamera, setShowExpiryCamera] = useState(null); // entry id being captured
  const [ocrLoading, setOcrLoading] = useState(null); // entry id loading OCR
  const [savedCount, setSavedCount] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(1);
  let nextId = useRef(2);

  const updateEntry = (id, field, value) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const addEntry = () => {
    setEntries(prev => [...prev, { id: nextId.current++, expiryDate: "", capturedImage: null, imagePath: null }]);
  };

  const removeEntry = (id) => {
    if (entries.length <= 1) return;
    setEntries(prev => prev.filter(e => e.id !== id));
  };

  const handleExpiryCapture = async (dataUrl) => {
    const entryId = showExpiryCamera;
    setShowExpiryCamera(null);
    updateEntry(entryId, "capturedImage", dataUrl);
    setOcrLoading(entryId);

    try {
      const { ok, data } = await safeFetch(`${API}/weekly-inventory/ocr-expiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_base64: dataUrl }),
      });
      if (!ok) throw new Error(data.detail || "Erro no OCR");
      updateEntry(entryId, "imagePath", data.image_path || null);
      if (data.extracted_date) {
        updateEntry(entryId, "expiryDate", data.extracted_date);
        toast.success("Validade extraída");
      } else {
        toast.info("Não extraiu a validade. Informe manualmente.");
      }
    } catch (err) {
      toast.error("Erro OCR: " + err.message);
    } finally {
      setOcrLoading(null);
    }
  };

  const handleSubmitAll = async (e) => {
    e.preventDefault();
    const valid = entries.filter(e => e.expiryDate);
    if (valid.length === 0) {
      toast.error("Informe pelo menos uma validade");
      return;
    }
    setSubmitting(true);
    let count = 0;
    for (const entry of valid) {
      await onSubmit({ ean, quantity: totalQuantity, expiry_date: entry.expiryDate, image_path: entry.imagePath });
      count++;
    }
    setSubmitting(false);
    setSavedCount(prev => prev + count);
    // Reset entries for potentially more
    setEntries([{ id: nextId.current++, expiryDate: "", capturedImage: null, imagePath: null }]);
    setTotalQuantity(1);
  };

  const formatDate = (d) => {
    if (!d) return "";
    const [y, m, dd] = d.split("-");
    return `${dd}/${m}/${y}`;
  };

  return (
    <>
      {showExpiryCamera !== null && (
        <ExpiryCameraCapture
          onCapture={handleExpiryCapture}
          onClose={() => setShowExpiryCamera(null)}
        />
      )}
      <form onSubmit={handleSubmitAll} className="bg-white border border-border rounded-sm p-4 space-y-3 animate-fadeIn" data-testid="lot-entry-form">
        {/* Product header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-xs text-muted-foreground">{ean}</p>
            <p className="font-semibold text-sm mt-0.5">{productName}</p>
            {!found && <p className="text-xs text-amber-600 mt-0.5">Produto não encontrado no sistema</p>}
            {savedCount > 0 && (
              <p className="text-xs text-green-600 mt-0.5 font-medium">{savedCount} lote{savedCount > 1 ? "s" : ""} salvo{savedCount > 1 ? "s" : ""}</p>
            )}
          </div>
          <button type="button" onClick={onCancel} className="p-1.5 hover:bg-muted rounded" data-testid="cancel-lot-entry">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Product quantity */}
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Quantidade</label>
          <input
            type="number"
            min="1"
            value={totalQuantity}
            onChange={(e) => setTotalQuantity(parseInt(e.target.value) || 1)}
            className="w-full border border-border rounded-sm px-3 py-2 text-sm font-mono focus:ring-1 focus:ring-primary focus:border-primary"
            data-testid="lot-total-quantity-input"
          />
        </div>

        {/* Entries list */}
        <div className="space-y-3">
          {entries.map((entry, idx) => (
            <div key={entry.id} className="border border-border/60 rounded-sm p-3 space-y-2 bg-muted/20">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground">Validade {idx + 1}</span>
                {entries.length > 1 && (
                  <button type="button" onClick={() => removeEntry(entry.id)} className="p-0.5 text-muted-foreground hover:text-destructive">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="flex gap-2 items-end">
                <button
                  type="button"
                  onClick={() => setShowExpiryCamera(entry.id)}
                  disabled={ocrLoading === entry.id}
                  className="flex items-center gap-1 px-2.5 py-2 bg-primary/10 text-primary border border-primary/20 rounded-sm text-xs font-medium hover:bg-primary/20 disabled:opacity-50 flex-shrink-0"
                  data-testid={`open-expiry-camera-btn-${entry.id}`}
                >
                  {ocrLoading === entry.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                  {ocrLoading === entry.id ? "Lendo..." : "Ler"}
                </button>
                <div className="flex-1 min-w-0">
                  <label className="text-[10px] font-medium text-muted-foreground block mb-0.5">Data</label>
                  <input
                    type="date"
                    value={entry.expiryDate}
                    onChange={(e) => updateEntry(entry.id, "expiryDate", e.target.value)}
                    className="w-full border border-border rounded-sm px-2 py-2 text-sm font-mono focus:ring-1 focus:ring-primary focus:border-primary"
                    data-testid={`lot-expiry-input-${entry.id}`}
                  />
                </div>
              </div>

              {entry.capturedImage && (
                <div className="flex items-center gap-2">
                  <img src={entry.capturedImage} alt="Validade" className="w-12 h-8 object-cover rounded border border-border" />
                  <span className="text-[10px] text-muted-foreground">
                    {entry.expiryDate ? formatDate(entry.expiryDate) : "Foto capturada"}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add another entry */}
        <button
          type="button"
          onClick={addEntry}
          className="w-full py-2 border border-dashed border-border rounded-sm text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-1"
          data-testid="add-another-expiry-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          Adicionar outra validade
        </button>

        {/* Submit */}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting || ocrLoading !== null}
            className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-sm text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="lot-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Salvar {entries.filter(e => e.expiryDate).length} Lote{entries.filter(e => e.expiryDate).length !== 1 ? "s" : ""}
          </button>
          {savedCount > 0 && (
            <button type="button" onClick={onCancel} className="px-4 py-2.5 border border-border rounded-sm text-sm font-medium hover:bg-muted" data-testid="finish-lots-btn">
              Concluir
            </button>
          )}
        </div>
      </form>
    </>
  );
}

// ==================== SESSION LIST VIEW ====================
function SessionListView({ sessions, onSelect, onCreate, loading }) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState(`Inventário ${new Date().toLocaleDateString("pt-BR")}`);
  const [newDate, setNewDate] = useState(new Date().toISOString().split("T")[0]);

  const handleCreate = async () => {
    await onCreate({ name: newName, date: newDate });
    setShowCreate(false);
  };

  return (
    <div className="space-y-4" data-testid="session-list-view">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold" style={{ fontFamily: "Manrope" }}>Sessões de Inventário</h2>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 rounded-sm text-sm font-medium hover:opacity-90"
          data-testid="create-session-btn"
        >
          <Plus className="w-4 h-4" />
          Nova Sessão
        </button>
      </div>

      {showCreate && (
        <div className="bg-white border border-border rounded-sm p-4 space-y-3 animate-fadeIn" data-testid="create-session-form">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nome da sessão"
            className="w-full border border-border rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:border-primary"
            data-testid="session-name-input"
          />
          <input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            className="w-full border border-border rounded-sm px-3 py-2 text-sm font-mono focus:ring-1 focus:ring-primary focus:border-primary"
            data-testid="session-date-input"
          />
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              className="flex-1 bg-primary text-primary-foreground py-2 rounded-sm text-sm font-medium hover:opacity-90"
              data-testid="confirm-create-session-btn"
            >
              Criar
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 border border-border rounded-sm text-sm hover:bg-muted"
              data-testid="cancel-create-session-btn"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Nenhuma sessão de inventário criada</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => (
            <button
              key={s.id}
              onClick={() => onSelect(s)}
              className="w-full bg-white border border-border rounded-sm p-4 text-left hover:border-primary/40 hover:shadow-sm transition-all"
              data-testid={`session-item-${s.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">{s.name}</p>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">
                    {new Date(s.date + "T12:00:00").toLocaleDateString("pt-BR")} 
                    {s.status === "finalized" && " — Finalizado"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">{s.unique_products || 0} produtos</p>
                  <p className="text-xs text-muted-foreground">{s.total_items || 0} unidades</p>
                </div>
              </div>
              <div className="mt-2">
                <span className={`inline-block px-2 py-0.5 rounded-sm text-xs font-medium ${
                  s.status === "active" ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"
                }`}>
                  {s.status === "active" ? "Ativa" : "Finalizada"}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== SESSION DETAIL VIEW ====================
function SessionDetailView({ session: initialSession, onBack, onRefresh }) {
  const [session, setSession] = useState(initialSession);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cameraActive, setCameraActive] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);
  const [manualEan, setManualEan] = useState("");
  const [editingLot, setEditingLot] = useState(null);
  const [editQty, setEditQty] = useState(0);
  const [viewingImage, setViewingImage] = useState(null);

  const fetchSession = useCallback(async () => {
    try {
      const { ok, data } = await safeFetch(`${API}/weekly-inventory/sessions/${initialSession.id}`);
      if (ok) { setSession(data); setLots(data.lots || []); }
    } catch (err) {
      toast.error("Erro ao carregar sessão");
    } finally {
      setLoading(false);
    }
  }, [initialSession.id]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  const handleScan = useCallback(async (ean) => {
    try {
      const { data } = await safeFetch(`${API}/products/lookup/${ean}`);
      setScannedProduct({ ean, name: data.name, found: data.found });
      setCameraActive(false);
      if (navigator.vibrate) navigator.vibrate(100);
      toast.success(`Lido: ${data.name}`);
    } catch {
      setScannedProduct({ ean, name: `Produto (${ean})`, found: false });
      setCameraActive(false);
    }
  }, []);

  const handleManualEan = async () => {
    const ean = manualEan.trim();
    if (!ean) return;
    await handleScan(ean);
    setManualEan("");
  };

  const handleLotSubmit = async ({ ean, quantity, expiry_date, image_path }) => {
    try {
      const { ok, data } = await safeFetch(`${API}/weekly-inventory/sessions/${session.id}/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ean, quantity, expiry_date, image_path }),
      });
      if (!ok) throw new Error(data.detail || "Erro ao registrar");
      toast.success(data.message);
      // Don't close scannedProduct - form stays open for next lot
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteLot = async (lotId) => {
    try {
      const { ok } = await safeFetch(`${API}/weekly-inventory/sessions/${session.id}/lots/${lotId}`, { method: "DELETE" });
      if (!ok) throw new Error("Erro ao remover");
      toast.success("Lote removido");
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateLotQty = async (lotId) => {
    try {
      const { ok } = await safeFetch(`${API}/weekly-inventory/sessions/${session.id}/lots/${lotId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quantity: editQty }),
      });
      if (!ok) throw new Error("Erro ao atualizar");
      toast.success("Quantidade atualizada");
      setEditingLot(null);
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleFinalize = async () => {
    if (!window.confirm("Finalizar esta sessão de inventário? Não será mais possível adicionar itens.")) return;
    try {
      const { ok } = await safeFetch(`${API}/weekly-inventory/sessions/${session.id}/finalize`, { method: "POST" });
      if (!ok) throw new Error("Erro ao finalizar");
      toast.success("Sessão finalizada");
      fetchSession();
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const isActive = session.status === "active";

  // Group lots by EAN for display
  const groupedLots = lots.reduce((acc, lot) => {
    if (!acc[lot.ean]) acc[lot.ean] = { ean: lot.ean, product_name: lot.product_name, lots: [] };
    acc[lot.ean].lots.push(lot);
    return acc;
  }, {});

  const formatDate = (d) => {
    if (!d) return "—";
    const parts = d.split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return d;
  };

  return (
    <div className="space-y-4" data-testid="session-detail-view">
      {/* Image viewer modal */}
      {viewingImage && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setViewingImage(null)}>
          <div className="relative max-w-lg w-full">
            <button onClick={() => setViewingImage(null)} className="absolute -top-10 right-0 text-white/80 hover:text-white" data-testid="close-image-viewer">
              <X className="w-6 h-6" />
            </button>
            <img
              src={`${API}/weekly-inventory/images/${viewingImage}`}
              alt="Foto da validade"
              className="w-full rounded-lg"
            />
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 hover:bg-muted rounded" data-testid="back-btn">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-semibold" style={{ fontFamily: "Manrope" }}>{session.name}</h2>
          <p className="text-xs text-muted-foreground font-mono">
            {formatDate(session.date)} — {isActive ? "Ativa" : "Finalizada"}
          </p>
        </div>
        {isActive && (
          <button
            onClick={handleFinalize}
            className="flex items-center gap-1.5 bg-amber-600 text-white px-3 py-1.5 rounded-sm text-xs font-medium hover:opacity-90"
            data-testid="finalize-session-btn"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Finalizar
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white border border-border rounded-sm p-3 text-center">
          <p className="text-xs text-muted-foreground">Produtos</p>
          <p className="text-xl font-bold font-mono">{Object.keys(groupedLots).length}</p>
        </div>
        <div className="bg-white border border-border rounded-sm p-3 text-center">
          <p className="text-xs text-muted-foreground">Lotes</p>
          <p className="text-xl font-bold font-mono">{lots.length}</p>
        </div>
        <div className="bg-white border border-border rounded-sm p-3 text-center">
          <p className="text-xs text-muted-foreground">Unidades</p>
          <p className="text-xl font-bold font-mono">{lots.reduce((a, l) => a + (l.quantity || 0), 0)}</p>
        </div>
      </div>

      {/* Scanner area - only when active */}
      {isActive && (
        <div className="space-y-3">
          {/* Camera toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => { setCameraActive(!cameraActive); setScannedProduct(null); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-sm text-sm font-medium transition-all ${
                cameraActive
                  ? "bg-destructive text-destructive-foreground"
                  : "bg-primary text-primary-foreground"
              }`}
              data-testid="toggle-camera-btn"
            >
              {cameraActive ? <CameraOff className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
              {cameraActive ? "Parar Câmera" : "Escanear Código"}
            </button>
          </div>

          {/* Barcode scanner */}
          {cameraActive && <BarcodeScanner onScan={handleScan} active={cameraActive} />}

          {/* Manual EAN input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={manualEan}
              onChange={(e) => setManualEan(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleManualEan()}
              placeholder="Digitar EAN manualmente..."
              className="flex-1 border border-border rounded-sm px-3 py-2 text-sm font-mono focus:ring-1 focus:ring-primary focus:border-primary"
              data-testid="manual-ean-input"
            />
            <button
              onClick={handleManualEan}
              className="px-3 py-2 bg-muted hover:bg-muted/80 rounded-sm text-sm"
              data-testid="manual-ean-submit"
            >
              <ScanBarcode className="w-4 h-4" />
            </button>
          </div>

          {/* Scanned product form */}
          {scannedProduct && (
            <LotEntryForm
              ean={scannedProduct.ean}
              productName={scannedProduct.name}
              found={scannedProduct.found}
              onSubmit={handleLotSubmit}
              onCancel={() => setScannedProduct(null)}
            />
          )}
        </div>
      )}

      {/* Lots list */}
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Itens Registrados ({lots.length})
        </h3>
        
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : Object.keys(groupedLots).length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Nenhum item escaneado ainda</p>
          </div>
        ) : (
          <div className="space-y-2">
            {Object.values(groupedLots).map((group) => (
              <div key={group.ean} className="bg-white border border-border rounded-sm overflow-hidden" data-testid={`lot-group-${group.ean}`}>
                <div className="px-3 py-2 bg-muted/30 border-b border-border">
                  <p className="font-semibold text-sm">{group.product_name}</p>
                  <p className="font-mono text-xs text-muted-foreground">{group.ean}</p>
                </div>
                {group.lots.map((lot) => (
                  <div key={lot.id} className="flex items-center gap-2 px-3 py-2 border-b border-border last:border-b-0">
                    <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs font-mono text-muted-foreground flex-shrink-0">
                      Val: {formatDate(lot.expiry_date)}
                    </span>
                    {lot.image_path && (
                      <button
                        onClick={() => setViewingImage(lot.image_path)}
                        className="p-0.5 text-primary/60 hover:text-primary"
                        title="Ver foto da validade"
                        data-testid={`view-photo-${lot.id}`}
                      >
                        <Camera className="w-3 h-3" />
                      </button>
                    )}
                    <span className="text-xs text-muted-foreground mx-0.5">—</span>
                    {editingLot === lot.id ? (
                      <div className="flex items-center gap-1 flex-1">
                        <input
                          type="number"
                          min="1"
                          value={editQty}
                          onChange={(e) => setEditQty(parseInt(e.target.value) || 0)}
                          className="w-16 border border-border rounded px-2 py-0.5 text-xs font-mono"
                          autoFocus
                        />
                        <button onClick={() => handleUpdateLotQty(lot.id)} className="text-green-600 p-0.5">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setEditingLot(null)} className="text-muted-foreground p-0.5">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="text-sm font-mono font-semibold flex-1">
                          {lot.quantity} un
                        </span>
                        {isActive && (
                          <div className="flex items-center gap-0.5">
                            <button
                              onClick={() => { setEditingLot(lot.id); setEditQty(lot.quantity); }}
                              className="p-1 hover:bg-muted rounded"
                              data-testid={`edit-lot-${lot.id}`}
                            >
                              <Edit3 className="w-3 h-3 text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => handleDeleteLot(lot.id)}
                              className="p-1 hover:bg-destructive/10 rounded"
                              data-testid={`delete-lot-${lot.id}`}
                            >
                              <Trash2 className="w-3 h-3 text-destructive" />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function WeeklyInventory() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);

  const fetchSessions = async () => {
    try {
      const { ok, data } = await safeFetch(`${API}/weekly-inventory/sessions`);
      if (ok) setSessions(data);
    } catch (err) {
      toast.error("Erro ao carregar sessões");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSessions(); }, []);

  const handleCreate = async ({ name, date }) => {
    try {
      const { ok, data } = await safeFetch(`${API}/weekly-inventory/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, date }),
      });
      if (!ok) throw new Error(data.detail || "Erro ao criar sessão");
      toast.success("Sessão criada");
      fetchSessions();
      setSelectedSession(data.session);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto" data-testid="weekly-inventory-page">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <ScanBarcode className="w-5 h-5 text-primary" />
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Manrope" }}>
            Inventário Semanal
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Escaneie os produtos e registre quantidades e validades por lote
        </p>
      </div>

      {selectedSession ? (
        <SessionDetailView
          session={selectedSession}
          onBack={() => { setSelectedSession(null); fetchSessions(); }}
          onRefresh={fetchSessions}
        />
      ) : (
        <SessionListView
          sessions={sessions}
          loading={loading}
          onSelect={setSelectedSession}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}
